import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { OrganizationNiche, TemplateError } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import FollowUpDelayDivider from "components/FollowUpDelayDivider";
import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import QuestionBox from "components/Styled/VAPage/QuestionBox";
import FollowupSelect from "components/TemplateScreen/FollowupSelect";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import {
  DAYS_TO_DELAY_FOLLOWUP,
  MAXIMUM_FOLLOWUP_ALLOWED,
} from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useRegisterSaveAsNew } from "utils/useRegisterSaveAsNew";

const styles = {
  chip: {
    color: "green.main",
    textTransform: "uppercase",
    width: "max-content",
    backgroundColor: "white",
    border: "1px solid",
  },
  addButton: {
    mt: 2,
    px: 2,
    width: "max-content",
    backgroundColor: "white",
    border: "1px solid",
    color: "black",
    "&:hover": {
      backgroundColor: "white",
      border: "1px solid",
      color: "black",
    },
  },
  closeIcon: {
    position: "absolute",
    right: "18px",
    top: "12px",
    fontSize: 18,
    color: grey[400],
  },
};

interface Props {
  nicheEdit: OrganizationNiche;
  setNicheEdit: Dispatch<SetStateAction<OrganizationNiche | null>>;
}

const SelfServeNicheEditDialog = ({ nicheEdit, setNicheEdit }: Props) => {
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { setOrganizationVaProfile, currentOrg, emailSettings } = useContext(
    OrganizationUserContext,
  );
  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    setCreateTemplateDialogOpen,
    setCurrentTemplateContentType,
    fetchOutreachTemplates,
  } = useContext(OutreachTemplatesContext);

  const [modifiedTemplates, setModifiedTemplates] = useState<Template[]>([]);
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);

  const modifiedTemplatesRef = useRef(modifiedTemplates);
  modifiedTemplatesRef.current = modifiedTemplates;
  const saveNicheRef = useRef<(templates?: Template[]) => Promise<void>>();

  // Swap the new template into this niche's selection and persist.
  useRegisterSaveAsNew((newTemplate, editedId) => {
    const updated = modifiedTemplatesRef.current.map((t) =>
      t.id === editedId ? newTemplate : t,
    );
    setModifiedTemplates(updated);
    saveNicheRef.current?.(updated);
  });

  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const selectedFollowups = modifiedTemplates?.slice(
    1,
    modifiedTemplates?.length,
  );

  const handleGenerateTemplate = (contentType: TemplateContentType) => {
    trackEvent("Generate Template Button Clicked", { Source: "VA Add Niche" });
    setCurrentTemplateContentType(contentType);
    setCreateTemplateDialogOpen(true);
  };

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...modifiedTemplates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = { ...template, delayDays: copy[index]?.delayDays };
      setModifiedTemplates(copy);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...modifiedTemplates];
    copy.splice(index, 1);
    setModifiedTemplates(copy);
  };

  const addFollowUp = () => {
    setModifiedTemplates([
      ...modifiedTemplates,
      { ...followupTemplates?.[0], delayDays: DAYS_TO_DELAY_FOLLOWUP },
    ]);
  };

  const handleDelayChange = (index: number, value: number) => {
    const copy = [...modifiedTemplates];
    if (copy[index]) {
      copy[index] = { ...copy[index], delayDays: value };
      setModifiedTemplates(copy);
    }
  };

  const initializeTemplates = () => {
    const settingTemplates =
      nicheEdit?.emailSetting?.emailSettingTemplates ??
      emailSettings?.emailSettingTemplates ??
      [];
    const idToDelay = new Map<number, number | null>();
    for (const st of settingTemplates) {
      if (st.templateId != null) {
        idToDelay.set(st.templateId, st.delayDays ?? null);
      }
    }

    const nicheTemplates = nicheEdit?.templates;
    if (nicheTemplates && nicheTemplates.length > 0) {
      // Prefer the delayDays on each Template; otherwise fall back to the
      // email_setting lookup, matching by template ID.
      setModifiedTemplates(
        nicheTemplates.map((t, idx) => {
          if (idx === 0) return { ...t, delayDays: undefined };
          if (t?.delayDays != null) return t;
          const found = t?.id != null ? idToDelay.get(t.id) : undefined;
          return { ...t, delayDays: found ?? DAYS_TO_DELAY_FOLLOWUP };
        }),
      );
      return;
    }

    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    const presetTemplates = emailSettings?.emailSettingTemplates || [];
    const matchedTemplates: Template[] = [];
    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push({
          ...template,
          delayDays:
            matchedTemplates.length === 0
              ? undefined
              : settingTemplate.delayDays ?? DAYS_TO_DELAY_FOLLOWUP,
        });
      }
    }
    if (matchedTemplates?.length > 0) {
      setModifiedTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setModifiedTemplates([
        { ...filteredOutreachTemplates[0], delayDays: undefined },
      ]);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
  };

  useEffect(() => {
    if (nicheEdit?.id) {
      initializeTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTemplates?.length, nicheEdit?.id]);

  const saveNiche = async (templatesOverride?: Template[]) => {
    if (!nicheEdit) return;
    const templatesToSave = templatesOverride ?? modifiedTemplates;
    setLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/${nicheEdit?.id}/with-automations`,
        "PUT",
        {},
        {
          templateIds: templatesToSave.map((template) => template.id),
          templateDelayDays: templatesToSave.map((t, idx) =>
            idx === 0 ? null : t.delayDays ?? null,
          ),
        },
        true,
      );
      const jsonMessage = await res.json();

      if (!res.ok && jsonMessage.field) {
        if (
          jsonMessage.field === "templates" &&
          jsonMessage.templateIdx !== undefined &&
          jsonMessage.templateIdx !== null
        ) {
          setTemplatesError({
            idx: jsonMessage.templateIdx,
            message: jsonMessage.message,
          });
        }
        return;
      }

      setAlert(
        `Successfully updated templates for ${nicheEdit?.name}`,
        "success",
      );
      setOrganizationVaProfile((prevProfile) => {
        if (!prevProfile || !prevProfile?.niches) return prevProfile;
        const updatedNiches = prevProfile.niches.map((niche) => {
          if (niche.id === nicheEdit.id) {
            return { ...niche, templates: templatesToSave };
          }
          return niche;
        });
        return { ...prevProfile, niches: updatedNiches };
      });

      setNicheEdit(null);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };
  saveNicheRef.current = saveNiche;

  const onClose = () => {
    setModifiedTemplates([]);
    setNicheEdit(null);
  };

  return (
    <Dialog
      open={Boolean(nicheEdit?.id)}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogContent sx={{ position: "relative" }}>
        <Typography variant="h4" textAlign="center" gutterBottom>
          {`Edit Templates for ${nicheEdit?.name}`}
        </Typography>

        <IconButton sx={styles.closeIcon} onClick={onClose}>
          <Box component="span" className="fa-regular fa-xmark" />
        </IconButton>

        <Grid
          container
          justifyContent="center"
          alignItems={"center"}
          flexDirection="column"
          sx={{ mb: 2 }}
          gap={2}
        >
          <QuestionBox>
            <Grid container alignItems="center" gap={1} sx={{ mb: 3 }}>
              <Chip label="Outreach" sx={styles.chip} variant="outlined" />
              <Typography color="textSecondary">
                Let's setup your outreach template
              </Typography>
            </Grid>

            {modifiedTemplates[0] ? (
              <OutreachSelect
                templates={modifiedTemplates}
                handleChange={handleTemplateChange}
                filteredOutreachTemplates={filteredOutreachTemplates}
                templateFromEmailSettings={templateFromEmailSettings}
                addNewOption={() =>
                  handleGenerateTemplate(TemplateContentType.OUTREACH)
                }
                templatesError={templatesError}
                nicheId={nicheEdit?.id}
              />
            ) : (
              <StyledLargeButton
                onClick={() =>
                  handleGenerateTemplate(TemplateContentType.OUTREACH)
                }
                disableElevation
                fullWidth
              >
                Generate Template
              </StyledLargeButton>
            )}
          </QuestionBox>

          <QuestionBox>
            <Grid container alignItems="center" gap={1} sx={{ mb: 3 }}>
              <Chip label="Follow-up" sx={styles.chip} variant="outlined" />
              <Typography color="textSecondary">
                Let's setup your followup templates
              </Typography>
            </Grid>

            {followupTemplates.length === 0 ? (
              <StyledLargeButton
                disableElevation
                disabled={outreachTemplates.length === 0}
                onClick={() =>
                  handleGenerateTemplate(TemplateContentType.FOLLOWUP)
                }
                fullWidth
              >
                Generate Follow-up
              </StyledLargeButton>
            ) : (
              <Grid container flexDirection="column">
                {selectedFollowups?.length > 0 &&
                  selectedFollowups.map((followup, index) => (
                    <Grid container key={index}>
                      <Grid container item xs={12} justifyContent="flex-start">
                        <FollowUpDelayDivider
                          days={followup?.delayDays ?? DAYS_TO_DELAY_FOLLOWUP}
                          onChange={(val) => handleDelayChange(index + 1, val)}
                          isFirst={index === 0}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FollowupSelect
                          followup={followup}
                          index={index}
                          filteredFollowUps={filteredFollowUps}
                          templateFromEmailSettings={templateFromEmailSettings}
                          handleTemplateChange={(e: CustomEvent, idx: number) =>
                            handleTemplateChange(e, idx)
                          }
                          onRemove={() => removeFollowUp(index + 1)}
                          templatesError={templatesError}
                        />
                      </Grid>
                    </Grid>
                  ))}

                {followupTemplates?.length > 0 &&
                  modifiedTemplates.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                    <Grid item xs={12}>
                      <StyledLargeButton
                        variant="outlined"
                        disableElevation
                        onClick={addFollowUp}
                        styles={styles.addButton}
                      >
                        <Box
                          component="i"
                          className="fa-regular fa-add"
                          sx={{ mr: 0.5 }}
                        />{" "}
                        Follow Up
                      </StyledLargeButton>
                    </Grid>
                  )}
              </Grid>
            )}
          </QuestionBox>
        </Grid>

        <Grid container justifyContent="center">
          <StyledLargeButton
            loading={loading}
            disableElevation
            onClick={() => saveNiche()}
          >
            Save Changes
          </StyledLargeButton>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default SelfServeNicheEditDialog;
